import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface DenyPickupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: {
    denialReason: string;
    counterProposedDate?: string;
    counterProposedTimeSlot?: string;
    userMessage?: string;
  }) => void;
  loading?: boolean;
}

const TIME_SLOTS = [
  "9:00 AM - 12:00 PM",
  "12:00 PM - 3:00 PM",
  "3:00 PM - 6:00 PM",
];

const DENIAL_REASONS = [
  { value: "date_not_available", label: "Date does not work" },
  { value: "time_not_available", label: "Time does not work" },
  { value: "other", label: "Other" },
];

export const DenyPickupDialog = ({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
}: DenyPickupDialogProps) => {
  const [denialReason, setDenialReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState("");
  const [counterProposedDate, setCounterProposedDate] = useState<Date | undefined>();
  const [counterProposedTimeSlot, setCounterProposedTimeSlot] = useState<string>("");
  const [userMessage, setUserMessage] = useState("");

  const handleConfirm = () => {
    const finalReason = denialReason === "other" ? otherReason : 
      DENIAL_REASONS.find(r => r.value === denialReason)?.label || denialReason;

    onConfirm({
      denialReason: finalReason,
      counterProposedDate: counterProposedDate 
        ? format(counterProposedDate, "yyyy-MM-dd") 
        : undefined,
      counterProposedTimeSlot: counterProposedTimeSlot || undefined,
      userMessage: userMessage || undefined,
    });

    // Reset form
    setDenialReason("");
    setOtherReason("");
    setCounterProposedDate(undefined);
    setCounterProposedTimeSlot("");
    setUserMessage("");
  };

  const isValid = denialReason && (denialReason !== "other" || otherReason.trim());
  const hasCounterProposal = counterProposedDate && counterProposedTimeSlot;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request Pickup Change</DialogTitle>
          <DialogDescription>
            Let us know why the proposed pickup doesn't work, and optionally suggest an alternative.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Denial Reason - Required */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              Reason for change <span className="text-destructive">*</span>
            </Label>
            <RadioGroup value={denialReason} onValueChange={setDenialReason}>
              {DENIAL_REASONS.map((reason) => (
                <div key={reason.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={reason.value} id={reason.value} />
                  <Label htmlFor={reason.value} className="font-normal cursor-pointer">
                    {reason.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            
            {denialReason === "other" && (
              <Textarea
                placeholder="Please explain why the proposed pickup doesn't work..."
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                className="mt-2"
                maxLength={500}
              />
            )}
          </div>

          {/* Counter-proposal - Optional */}
          <div className="space-y-3 p-4 rounded-lg bg-muted/50 border">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-primary" />
              <Label className="text-sm font-medium">Suggest Alternative (Recommended)</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Help us find a time that works for you by proposing an alternative.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {/* Alternative Date */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Preferred Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !counterProposedDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {counterProposedDate ? format(counterProposedDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={counterProposedDate}
                      onSelect={setCounterProposedDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Alternative Time */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Preferred Time</Label>
                <Select value={counterProposedTimeSlot} onValueChange={setCounterProposedTimeSlot}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Validation hint */}
            {(counterProposedDate && !counterProposedTimeSlot) || 
             (!counterProposedDate && counterProposedTimeSlot) ? (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Please select both date and time for a counter-proposal
              </p>
            ) : null}
          </div>

          {/* Optional Message */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Additional Message (Optional)</Label>
            <Textarea
              placeholder="Any additional information for the pickup team..."
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || loading || 
              (!!counterProposedDate && !counterProposedTimeSlot) || 
              (!counterProposedDate && !!counterProposedTimeSlot)}
          >
            {loading ? "Submitting..." : hasCounterProposal ? "Submit Counter-Proposal" : "Submit Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
